import CheckPermission from '../../../hooks/CheckPermission';
import React, { useState } from "react";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { useDispatch, useSelector } from "react-redux";
import { Dialog } from "primereact/dialog";
import toast from "react-hot-toast";
import { FaCheckDouble, FaUserPlus } from "react-icons/fa";

import TextInput from "../../../shared/TextInput";
import DropdownField from "../../../shared/DropdownField";
import CustomButton from "../../../shared/CustomButton";
import { addPatient } from "../../../redux/slices/WardManagementSlice";
import { useTranslation } from "react-i18next";

const addPatientValidationSchema = Yup.object({
  patientName: Yup.string()
    .required("Patient name is required")
    .min(2, "Patient name must be at least 2 characters")
    .max(100, "Patient name must be less than 100 characters"),
  wardId: Yup.number().required("Ward selection is required"),
  dateOfBirth: Yup.date().required("Date of birth is required"),
  gender: Yup.string().required("Gender is required"),
  fileNo: Yup.string().required("File number is required"),
  phoneNo: Yup.string().required("Phone number is required"),
  priority: Yup.string().required("Priority is required"),
  reason: Yup.string().required("Reason for admission is required"),
  patientStatus: Yup.string().required("Patient status is required"),
  waitingTime: Yup.number().when("patientStatus", {
    is: "Waiting",
    then: (schema) =>
      schema
        .required("Waiting time is required")
        .min(0, "Waiting time must be positive"),
    otherwise: (schema) => schema.nullable(),
  }),
  bedId: Yup.number().when("patientStatus", {
    is: "Admitted",
    then: (schema) => schema.required("Bed selection is required"),
    otherwise: (schema) => schema.nullable(),
  }),
});

const AddPatientDialog = ({ visible, onHide, preselectedWardId = null }) => {
  const { hasPermission } = CheckPermission();
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);
  const { wards, beds } = useSelector((state) => state.wardManagement);
  const { t, i18n } = useTranslation();
  const initialValues = {
    patientName: "",
    wardId: preselectedWardId || "",
    dateOfBirth: "",
    gender: "Male",
    fileNo: "",
    phoneNo: "",
    priority: "Normal",
    reason: "",
    patientStatus: "Waiting",
    waitingTime: 0,
    bedId: "",
  };

  const wardOptions = wards.map((ward) => ({
    label: ward.name,
    value: ward.id,
  }));

  const genderOptions = [
    { label: "Male", value: "Male", labelAr: "ذكر", valueAr: "ذكر" },
    { label: "Female", value: "Female", labelAr: "أنثى", valueAr: "أنثى" },
    { label: "Other", value: "Other", labelAr: "آخر", valueAr: "آخر" },
  ];

  const priorityOptions = [
    { label: "Normal", value: "Normal" },
    { label: "Medium", value: "Medium" },
    { label: "High", value: "High" },
  ];

  const patientStatusOptions = [
    { label: "Waiting", value: "Waiting" },
    { label: "Admitted", value: "Admitted" },
    { label: "Discharged", value: "Discharged" },
    { label: "Transferred", value: "Transferred" },
  ];

  // Get available beds for the selected ward
  const getAvailableBeds = (wardId) => {
    return beds
      .filter(
        (bed) => bed.wardId === wardId && bed.availability === "Available"
      )
      .map((bed) => ({
        label: bed.name,
        value: bed.id,
      }));
  };

  const handleSubmit = async (values, { resetForm }) => {
    if (!hasPermission('patientAdmission', 'create') && !hasPermission('ward', 'create')) return;
    setIsLoading(true);
    try {
      dispatch(
        addPatient({
          name: values.patientName,
          wardId: values.wardId,
          dateOfBirth: values.dateOfBirth,
          gender: values.gender,
          fileNo: values.fileNo,
          phoneNo: values.phoneNo,
          priority: values.priority,
          reason: values.reason,
          patientStatus: values.patientStatus,
          waitingTime:
            values.patientStatus === "Waiting" ? values.waitingTime : 0,
          bedId: values.patientStatus === "Admitted" ? values.bedId : null,
        })
      );

      toast.success("Patient added successfully!");
      resetForm();
      onHide();
    } catch (error) {
      toast.error("Failed to add patient");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog
      visible={visible}
      onHide={onHide}
      header={
        <div className="flex items-center gap-2">
          <FaUserPlus className="text-primary" />
          <span>Add New Patient</span>
        </div>
      }
      style={{ width: "600px" }}
      modal
      className="p-fluid"
    >
      {wards.length === 0 ? (
        <div className="text-center py-8">
          <p className="text-gray-500 mb-4">
            No wards available. Please create a ward first.
          </p>
        </div>
      ) : (
        <Formik
          initialValues={initialValues}
          validationSchema={addPatientValidationSchema}
          onSubmit={handleSubmit}
        >
          {(props) => (
            <Form>
              <div className="space-y-4">
                <TextInput
                  name="patientName"
                  label="Patient Name*"
                  placeholder="Enter patient name"
                />

                <DropdownField
                  name="wardId"
                  label="Select Ward*"
                  placeholder="Select ward"
                  options={wardOptions}
                  optionLabel="label"
                  optionValue="value"
                />

                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    name="dateOfBirth"
                    label="Date of Birth*"
                    type="date"
                  />

                  <DropdownField
                    name="gender"
                    label="Gender*"
                    placeholder="Select gender"
                    options={genderOptions}
                    optionLabel={i18n.language == "en" ? "label" : "labelAr"}
                    optionValue={"value"}
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <TextInput
                    name="fileNo"
                    label="MRN*"
                    placeholder="Enter MRN"
                  />

                  <TextInput
                    name="phoneNo"
                    label="Phone Number*"
                    placeholder="Enter phone number"
                  />
                </div>

                <DropdownField
                  name="priority"
                  label="Priority*"
                  placeholder="Select priority"
                  options={priorityOptions}
                  optionLabel="label"
                  optionValue="value"
                />

                <DropdownField
                  name="patientStatus"
                  label="Patient Status*"
                  placeholder="Select patient status"
                  options={patientStatusOptions}
                  optionLabel="label"
                  optionValue="value"
                />

                {/* Conditional field for waiting time */}
                {props.values.patientStatus === "Waiting" && (
                  <TextInput
                    name="waitingTime"
                    label="Waiting Time (Days)*"
                    type="number"
                    step="any"
                    placeholder="Enter waiting time in days"
                    min="0"
                  />
                )}

                {/* Conditional field for bed selection */}
                {props.values.patientStatus === "Admitted" && (
                  <DropdownField
                    name="bedId"
                    label="Select Bed*"
                    placeholder="Select available bed"
                    options={getAvailableBeds(props.values.wardId)}
                    optionLabel="label"
                    optionValue="value"
                  />
                )}

                <TextInput
                  name="reason"
                  label="Reason for Admission*"
                  placeholder="Enter reason for admission"
                  multiline
                  rows={3}
                />

                <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
                  <CustomButton
                    label="Cancel"
                    buttonType="reset"
                    onClick={onHide}
                  />
                  {(hasPermission('patientAdmission', 'create') || hasPermission('ward', 'create')) && (
                    <CustomButton
                      type="submit"
                      label="Add Patient"
                      icon={<FaCheckDouble size={15} className="me-2" />}
                      buttonType="primary"
                      loading={isLoading}
                      disabled={isLoading}
                    />
                  )}
                </div>
              </div>
            </Form>
          )}
        </Formik>
      )}
    </Dialog>
  );
};

export default AddPatientDialog;
